import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { motionReduced, useSettingsStore } from '../../state/settings.js';
import './StarBorder.css';

export interface StarBorderProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'color' | 'children'
> {
  children: ReactNode;
  /** Star head colour. */
  color?: string;
  /** Colour the trail fades from. */
  trailColor?: string;
  /** Seconds per lap. */
  duration?: number;
  direction?: 'clockwise' | 'counterclockwise';
  /** Stars circling at once (1-6). */
  stars?: number;
  /** Trail length as a share of the star's segment of the outline (0-1). */
  trailLength?: number;
  /** Outline thickness in px. */
  thickness?: number;
  /** Corner radius in px. */
  radius?: number;
  /** Glow strength (0-1). */
  glow?: number;
  /** `lap` speeds the star up for a lap while hovered; `brighten` lifts the glow. */
  hover?: 'lap' | 'brighten' | 'none';
  clickPulse?: boolean;
  theme?: 'dark' | 'light';
}

const THEMES = {
  dark: { surface: '#12151d', text: '#f4f1ea', track: 'rgba(255, 255, 255, 0.12)' },
  light: { surface: '#fbf8f1', text: '#1f2430', track: 'rgba(0, 0, 0, 0.12)' },
};

/** A button whose outline is circled by a star with a fading trail; holds still under Reduce motion. */
export default function StarBorder({
  children,
  color = '#ffffff',
  trailColor = '#7c5cff',
  duration = 6,
  direction = 'clockwise',
  stars = 1,
  trailLength = 0.5,
  thickness = 1,
  radius = 12,
  glow = 0.6,
  hover = 'lap',
  clickPulse = false,
  theme = 'dark',
  className,
  style,
  onClick,
  onPointerEnter,
  onPointerLeave,
  ...rest
}: StarBorderProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const hovering = useRef(false);
  const [pulsing, setPulsing] = useState(false);
  const reduceSetting = useSettingsStore((s) => s.reduceMotion);

  const count = Math.min(6, Math.max(1, Math.round(stars)));
  const segment = 360 / count;
  const trail = segment * Math.min(1, Math.max(0.05, trailLength));
  const gradient =
    `repeating-conic-gradient(from var(--sb-angle), transparent 0deg, ` +
    `transparent ${(segment - trail).toFixed(2)}deg, ${trailColor} ${(segment - trail * 0.6).toFixed(2)}deg, ` +
    `${color} ${segment.toFixed(2)}deg)`;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const system = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (motionReduced(reduceSetting, system)) {
      el.style.setProperty('--sb-angle', '300deg');
      return;
    }
    const sign = direction === 'clockwise' ? 1 : -1;
    let frame = 0;
    let last = performance.now();
    let angle = 0;
    let boost = 1;
    let lapLeft = 0;
    let wasHovering = false;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (hover === 'lap' && hovering.current && !wasHovering) lapLeft = 360;
      wasHovering = hovering.current;
      boost = lapLeft > 0 ? 3 : 1;
      const step = (360 / Math.max(0.5, duration)) * dt * boost;
      lapLeft = Math.max(0, lapLeft - step);
      angle = (angle + sign * step + 360) % 360;
      el.style.setProperty('--sb-angle', `${angle.toFixed(2)}deg`);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduceSetting, direction, duration, hover]);

  const palette = THEMES[theme];
  const vars = {
    '--sb-gradient': gradient,
    '--sb-thickness': `${thickness}px`,
    '--sb-radius': `${radius}px`,
    '--sb-glow': glow,
    '--sb-surface': palette.surface,
    '--sb-text': palette.text,
    '--sb-track': palette.track,
  } as CSSProperties;

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={['star-border', pulsing && 'is-pulsing', className].filter(Boolean).join(' ')}
      style={{ ...vars, ...style }}
      onPointerEnter={(event) => {
        hovering.current = true;
        onPointerEnter?.(event);
      }}
      onPointerLeave={(event) => {
        hovering.current = false;
        onPointerLeave?.(event);
      }}
      onClick={(event) => {
        if (clickPulse) {
          setPulsing(true);
          window.setTimeout(() => setPulsing(false), 450);
        }
        onClick?.(event);
      }}
    >
      <span className="star-border-glow" aria-hidden="true" />
      <span className="star-border-ring" aria-hidden="true" />
      <span className="star-border-inner">{children}</span>
    </button>
  );
}
